import type {
  AggregatePanelDataToolResult,
  ChatMessage,
  ChoiceCardOption,
  ChoiceCardVariant,
  FilterField,
  LibraryGap,
  ProposedPanel,
  QaToolResult,
  QueryPanelDataToolResult,
  ThinPreviewWarning,
} from '@/types/Dashboards.types'
import type { CredentialOption } from '@/types/DashboardV1.types'

// ─── Anthropic raw message shapes ────────────────────────────────────────────

interface AnthropicTextBlock {
  type: 'text'
  text: string
}

interface AnthropicToolUseBlock {
  type: 'tool_use'
  /** Anthropic tool_use block id — correlates with tool_result's tool_use_id. */
  id?: string
  name: string
  input: Record<string, unknown>
}

interface AnthropicToolResultBlock {
  type: 'tool_result'
  tool_use_id?: string
  content?: unknown
}

type AnthropicContentBlock = AnthropicTextBlock | AnthropicToolUseBlock | AnthropicToolResultBlock

interface AnthropicMessage {
  role: 'user' | 'assistant'
  content: string | AnthropicContentBlock[]
}

// ─── Choice card sentinel parser ──────────────────────────────────────────────

const CHOICE_CARD_PATTERN = /\[\[([^\]]+)\]\]/g

function parseChoiceCards(text: string): {
  type: 'text' | 'choice_cards'
  content: string
  choiceCards?: ChoiceCardOption[]
  choiceCardVariant?: ChoiceCardVariant
} {
  const labels: string[] = []
  let match: RegExpExecArray | null

  CHOICE_CARD_PATTERN.lastIndex = 0
  while ((match = CHOICE_CARD_PATTERN.exec(text)) !== null) {
    labels.push(match[1])
  }

  if (labels.length > 0) {
    const content = text.replace(/\[\[[^\]]+\]\]/g, '').trim()
    const choiceCards: ChoiceCardOption[] = labels.map(label => ({ id: label, label }))
    return { type: 'choice_cards', content, choiceCards, choiceCardVariant: 'pill' }
  }

  return { type: 'text', content: text }
}

// ─── tool_result → user-visible selection text ───────────────────────────────

function extractToolResultUserText(block: AnthropicToolResultBlock): string | null {
  const c = block.content
  if (c == null) return null
  if (typeof c === 'string') {
    const t = c.trim()
    return t.length > 0 ? t : null
  }
  if (Array.isArray(c)) {
    const parts: string[] = []
    for (const item of c) {
      if (item !== null && typeof item === 'object') {
        const o = item as Record<string, unknown>
        if (o.type === 'text' && typeof o.text === 'string') parts.push(o.text)
      }
    }
    const joined = parts.join('\n').trim()
    return joined.length > 0 ? joined : null
  }
  if (typeof c === 'object' && 'text' in c && typeof (c as { text: unknown }).text === 'string') {
    const t = (c as { text: string }).text.trim()
    return t.length > 0 ? t : null
  }
  return null
}

// ─── present_filters → filter_card ───────────────────────────────────────────

function looseFilterField(item: unknown): FilterField | null {
  if (typeof item !== 'object' || item === null) return null
  const o = item as Record<string, unknown>
  const filterId =
    typeof o.filterId === 'string' ? o.filterId : typeof o.id === 'string' ? o.id : null
  if (!filterId) return null
  const label = typeof o.label === 'string' ? o.label : filterId
  const required = o.required === true
  const type =
    o.type === 'select' || o.type === 'checkbox_group' || o.type === 'text' ? o.type : 'text'
  let value: string | string[] = ''
  if (Array.isArray(o.value)) {
    value = o.value.filter((v): v is string => typeof v === 'string')
  } else if (typeof o.value === 'string') {
    value = o.value
  }
  const options = Array.isArray(o.options)
    ? o.options
        .map(opt => {
          if (
            typeof opt === 'object' &&
            opt !== null &&
            typeof (opt as { label?: unknown }).label === 'string' &&
            typeof (opt as { value?: unknown }).value === 'string'
          ) {
            return { label: (opt as { label: string }).label, value: (opt as { value: string }).value }
          }
          return null
        })
        .filter((x): x is { label: string; value: string } => x !== null)
    : undefined
  return { filterId, label, value, required, type, options }
}

function mapSdkFiltersToFilterFields(sdkFilters: unknown): FilterField[] {
  if (!Array.isArray(sdkFilters)) return []
  const out: FilterField[] = []
  sdkFilters.forEach((item, i) => {
    if (typeof item !== 'object' || item === null) return
    const o = item as Record<string, unknown>
    const filterId = typeof o.filterId === 'string' ? o.filterId : `filter_${i}`
    const value = typeof o.value === 'string' ? o.value : ''
    const label =
      typeof o.label === 'string'
        ? o.label
        : filterId.replace(/_/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase())
    out.push({
      filterId,
      label,
      value,
      required: false,
      type: 'text',
    })
  })
  return out
}

function parsePresentFiltersInput(input: Record<string, unknown>): { content: string; filters: FilterField[] } {
  const introText = typeof input.introText === 'string' ? input.introText : ''
  const direct = input.filters ?? input.fields
  if (Array.isArray(direct) && direct.length > 0) {
    const filters = direct.map(item => looseFilterField(item)).filter((x): x is FilterField => x !== null)
    if (filters.length > 0) return { content: introText, filters }
  }
  return { content: introText, filters: mapSdkFiltersToFilterFields(input.sdkFilters) }
}

// ─── present_credential → credential_card (best-effort from stored input) ─────

function presentCredentialToMessage(input: Record<string, unknown>): ChatMessage | null {
  const credentialId = typeof input.credentialId === 'string' ? input.credentialId : null
  if (!credentialId) return null
  const appName = typeof input.appName === 'string' ? input.appName : ''
  const credentialName =
    typeof input.credentialName === 'string'
      ? input.credentialName
      : typeof input.nickname === 'string'
        ? input.nickname
        : appName || 'Credential'
  return {
    role: 'assistant',
    type: 'credential_card',
    content: typeof input.introText === 'string' ? input.introText : '',
    credentialId,
    appName,
    credentialName,
    logoUrl: typeof input.logoUrl === 'string' ? input.logoUrl : '',
    credentialType: typeof input.credentialType === 'string' ? input.credentialType : 'Credential',
    expiresAt: typeof input.expiresAt === 'string' ? input.expiresAt : null,
    status: 'active',
    timestamp: '',
  }
}

// ─── present_credential → CredentialOption (for availableCredentials Redux) ───
//
// CEO v1 constraint (2026-04-28): only apiKey credentials are supported by the
// harvest flow today. The `present_credential` tool-call payload does not carry a
// `loginMethodCode` field in v1. We treat every present_credential as apiKey for
// now. If future SDK auth types (e.g. usernamePassword, OAuth) are added, this
// function must be revisited to filter or discriminate by loginMethodCode.
// Non-apiKey credentials are NOT silently swallowed — this comment documents the
// intentional assumption so a future SDK auth type forces a FE revisit.
//
// `expiresAt` → `expiresInDays` is computed by the caller (DashboardChatService)
// because Date.now() is a side effect. This function returns the raw expiresAt
// string so the caller can perform the conversion with a stable reference date.

export interface RawCredentialOption {
  id: string
  label: string
  subtitle?: string
  /** ISO-8601 string from the present_credential payload; caller converts to expiresInDays. */
  expiresAt: string | null
  /**
   * Pre-computed availability summary from the BE. Passed through unchanged
   * to CredentialOption. Absent on credentials sourced from the AI-driven
   * present_credential tool-call path (v1 — only the prefetch path carries it).
   * Phase E Slice 2.4 — TDD §5.2.
   */
  availabilitySummary?: import('@/types/DashboardV1.types').CredentialAvailabilitySummary
}

/**
 * Extracts a RawCredentialOption from a present_credential tool input.
 * Returns null if credentialId is missing (same guard as presentCredentialToMessage).
 */
function presentCredentialToOption(input: Record<string, unknown>): RawCredentialOption | null {
  const id = typeof input.credentialId === 'string' ? input.credentialId : null
  if (!id) return null
  const appName = typeof input.appName === 'string' ? input.appName : ''
  const label =
    typeof input.credentialName === 'string'
      ? input.credentialName
      : typeof input.nickname === 'string'
        ? input.nickname
        : appName || 'Credential'
  const subtitle = typeof input.subtitle === 'string' ? input.subtitle : undefined
  const expiresAt = typeof input.expiresAt === 'string' ? input.expiresAt : null
  return { id, label, subtitle, expiresAt }
}

/**
 * Converts a RawCredentialOption to a CredentialOption by computing expiresInDays
 * from the provided reference date. Exported so callers can apply a stable now().
 */
export function rawToCredentialOption(
  raw: RawCredentialOption,
  nowMs: number = Date.now()
): CredentialOption {
  let expiresInDays: number | undefined
  if (raw.expiresAt != null) {
    const diffMs = new Date(raw.expiresAt).getTime() - nowMs
    const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24))
    expiresInDays = days > 0 ? days : 0
  }
  return {
    id: raw.id,
    label: raw.label,
    ...(raw.subtitle !== undefined ? { subtitle: raw.subtitle } : {}),
    ...(expiresInDays !== undefined ? { expiresInDays } : {}),
    ...(raw.availabilitySummary !== undefined ? { availabilitySummary: raw.availabilitySummary } : {}),
  }
}

// ─── Type guards ──────────────────────────────────────────────────────────────

function isToolUseBlock(block: AnthropicContentBlock): block is AnthropicToolUseBlock {
  return block.type === 'tool_use'
}

function isTextBlock(block: AnthropicContentBlock): block is AnthropicTextBlock {
  return block.type === 'text'
}

function isToolResultBlock(block: AnthropicContentBlock): block is AnthropicToolResultBlock {
  return block.type === 'tool_result'
}

function isAnthropicMessage(value: unknown): value is AnthropicMessage {
  if (typeof value !== 'object' || value === null) return false
  const obj = value as Record<string, unknown>
  return (obj.role === 'user' || obj.role === 'assistant') && 'content' in obj
}

function toolUseToAssistantMessage(block: AnthropicToolUseBlock): ChatMessage | null {
  const input = block.input ?? {}

  if (block.name === 'propose_panels') {
    const introText = typeof input.introText === 'string' ? input.introText : ''
    const panels = Array.isArray(input.panels) ? (input.panels as ProposedPanel[]) : []

    // Extract thin-preview warning metadata if present (§3d.5 / brand-guide §24).
    // Backend sets this when totalRowCount < 20 across all domains.
    //
    // Defensive parse: if rowCount is missing or non-numeric, the entire warning
    // is treated as null. Previously we substituted rowCount=0, which rendered
    // "Your last 14 days only had 0 enrollment records" — misleading copy that
    // implied zero data when in reality the payload was malformed. Prefer no
    // callout over a callout with a lie.
    let thinPreviewWarning: ThinPreviewWarning | null = null
    if (
      typeof input.thinPreviewWarning === 'object' &&
      input.thinPreviewWarning !== null
    ) {
      const raw = input.thinPreviewWarning as Record<string, unknown>
      if (typeof raw.rowCount === 'number') {
        const unreliablePanels = Array.isArray(raw.unreliablePanels)
          ? (raw.unreliablePanels as unknown[])
              .filter((p): p is string => typeof p === 'string')
          : []
        thinPreviewWarning = { rowCount: raw.rowCount, unreliablePanels }
      }
      // else: leave thinPreviewWarning null — malformed payloads do not render a callout.
    }

    // Extract library gap envelope if present (TDD §4.8.3, §3 L6 locked shape).
    // Dashboard-era only — set by the AI when the user requests a panel type the
    // library cannot fulfill. Defensive parse: both fields must be present with
    // correct types; malformed payloads produce null (no banner).
    let libraryGap: LibraryGap | null = null
    if (
      typeof input.libraryGap === 'object' &&
      input.libraryGap !== null
    ) {
      const raw = input.libraryGap as Record<string, unknown>
      if (typeof raw.requestedDescription === 'string' && raw.requestedDescription.trim().length > 0) {
        libraryGap = {
          requestedDescription: raw.requestedDescription,
          // closestHintId may be null per TDD §3 L6 — accept both null and string.
          closestHintId: typeof raw.closestHintId === 'string' ? raw.closestHintId : null,
        }
      }
      // else: leave libraryGap null — malformed or empty payloads do not render a banner.
    }

    return {
      role: 'assistant',
      type: 'panel_proposals',
      content: introText,
      panels,
      thinPreviewWarning,
      libraryGap,
      timestamp: '',
    }
  }

  if (block.name === 'present_choices') {
    const rawVariant = typeof input.variant === 'string' ? input.variant : 'pill'
    const variant: ChoiceCardVariant =
      rawVariant === 'source_card' ? rawVariant : 'pill'
    const rawChoices = Array.isArray(input.choices)
      ? (input.choices as Array<{ id: string; label: string; icon?: string; description?: string }>)
      : []
    const choiceCards: ChoiceCardOption[] = rawChoices.map(c => ({
      id: c.id,
      label: c.label,
      icon: c.icon,
      description: c.description,
    }))
    const introText = typeof input.introText === 'string' ? input.introText : ''
    return {
      role: 'assistant',
      type: 'choice_cards',
      content: introText,
      choiceCards,
      choiceCardVariant: variant,
      timestamp: '',
    }
  }

  if (block.name === 'present_filters') {
    const { content, filters } = parsePresentFiltersInput(input)
    return {
      role: 'assistant',
      type: 'filter_card',
      content,
      appName: typeof input.appName === 'string' ? input.appName : '',
      filters,
      timestamp: '',
    }
  }

  if (block.name === 'present_credential') {
    return presentCredentialToMessage(input)
  }

  // confirm_sources, confirm_panels, and unknown tools — no restored bubble
  return null
}

// ─── Q&A tool result extractor ───────────────────────────────────────────────
//
// Extracts QaToolResult metadata from a tool_result block whose corresponding
// tool_use was a Q&A tool. Used during session-restore (parseAnthropicMessages)
// to reconstruct banners on restored chat history.
//
// Supported tools:
//   - query_panel_data   (Track 3 Slice 2.2) → QueryPanelDataToolResult
//   - aggregate_panel_data (Track 3 Slice 3.2) → AggregatePanelDataToolResult
//
// TDD §3.7.

/**
 * The Q&A tool names that produce structured result metadata.
 * Used to decide whether a tool_result block carries QaToolResult data.
 */
const QA_TOOL_NAMES_WITH_RESULT: ReadonlySet<string> = new Set([
  'query_panel_data',
  'aggregate_panel_data',
])

/**
 * Extracts the shared cacheMiss field from a raw tool_result payload.
 * Returns undefined when absent or malformed.
 */
function extractCacheMiss(
  raw: Record<string, unknown>,
): { reason: 'never_synced' | 'domain_not_loaded' } | undefined {
  if (
    typeof raw.cacheMiss === 'object' &&
    raw.cacheMiss !== null &&
    typeof (raw.cacheMiss as Record<string, unknown>).reason === 'string'
  ) {
    const reason = (raw.cacheMiss as Record<string, unknown>).reason
    if (reason === 'never_synced' || reason === 'domain_not_loaded') {
      return { reason }
    }
  }
  return undefined
}

/**
 * Attempts to parse a `tool_result` block's content as a QueryPanelDataToolResult.
 *
 * Returns null when the required fields (totalMatched, truncated) are absent.
 */
function extractQueryPanelDataResult(
  raw: Record<string, unknown>,
): QueryPanelDataToolResult | null {
  if (typeof raw.totalMatched !== 'number' || typeof raw.truncated !== 'boolean') return null
  const cacheMiss = extractCacheMiss(raw)
  return {
    tool: 'query_panel_data',
    totalMatched: raw.totalMatched,
    truncated: raw.truncated,
    ...(cacheMiss !== undefined ? { cacheMiss } : {}),
  }
}

/**
 * Attempts to parse a `tool_result` block's content as an AggregatePanelDataToolResult.
 *
 * Returns null when the required fields (result, totalRowsConsidered) are absent or malformed.
 *
 * The FE only needs the `result.kind` discriminator and `hasOtherBucket` — not the full
 * `groups[]` array (which the AI's prose already describes). Defensive parse: if `result`
 * is absent or has an unrecognised kind, return null so the message renders as plain prose
 * without a banner.
 */
function extractAggregatePanelDataResult(
  raw: Record<string, unknown>,
): AggregatePanelDataToolResult | null {
  if (typeof raw.totalRowsConsidered !== 'number') return null

  // Parse strategy — default to 'in_memory' when absent (older BE may omit it).
  const strategy: 'in_memory' | 'jsonb_sql' =
    raw.strategy === 'jsonb_sql' ? 'jsonb_sql' : 'in_memory'

  // Parse result discriminator.
  if (typeof raw.result !== 'object' || raw.result === null || Array.isArray(raw.result)) return null
  const resultRaw = raw.result as Record<string, unknown>

  let result: AggregatePanelDataToolResult['result']
  if (resultRaw.kind === 'scalar') {
    const value = typeof resultRaw.value === 'number' ? resultRaw.value : null
    result = { kind: 'scalar', value }
  } else if (resultRaw.kind === 'grouped') {
    // groupCount: number of named groups in the result.
    const groupCount = typeof resultRaw.groupCount === 'number' ? resultRaw.groupCount :
      // Fallback: derive from groups[] array length when present (older BE shape).
      (Array.isArray(resultRaw.groups) ? (resultRaw.groups as unknown[]).length : 0)
    // hasOtherBucket: explicit boolean, or inferred from otherBucket presence.
    const hasOtherBucket =
      resultRaw.hasOtherBucket === true ||
      (typeof resultRaw.otherBucket === 'object' && resultRaw.otherBucket !== null)
    result = { kind: 'grouped', groupCount, hasOtherBucket }
  } else {
    // Unknown kind — render as plain prose, no banner.
    return null
  }

  const cacheMiss = extractCacheMiss(raw)
  return {
    tool: 'aggregate_panel_data',
    strategy,
    totalRowsConsidered: raw.totalRowsConsidered,
    result,
    ...(cacheMiss !== undefined ? { cacheMiss } : {}),
  }
}

/**
 * Attempts to parse a `tool_result` block's content as QaToolResult when the
 * corresponding tool_use was a known Q&A tool.
 *
 * Returns null when:
 * - The tool name is not a known Q&A tool.
 * - The content is not a parseable object (e.g., an error result or string).
 * - The required fields for the specific tool are absent.
 */
function extractQaToolResult(
  block: AnthropicToolResultBlock,
  tool: string,
): QaToolResult | null {
  /* istanbul ignore next -- @preserve defensive: caller already filters by QA_TOOL_NAMES_WITH_RESULT before invoking this helper */
  if (!QA_TOOL_NAMES_WITH_RESULT.has(tool)) return null
  const content = block.content
  if (typeof content !== 'object' || content === null || Array.isArray(content)) return null
  const raw = content as Record<string, unknown>

  if (tool === 'query_panel_data') {
    return extractQueryPanelDataResult(raw)
  }
  if (tool === 'aggregate_panel_data') {
    return extractAggregatePanelDataResult(raw)
  }
  /* istanbul ignore next -- @preserve defensive: QA_TOOL_NAMES_WITH_RESULT is the closed set {'query_panel_data', 'aggregate_panel_data'} — every member is dispatched above */
  return null
}

/**
 * Maps a QaToolResult discriminator to the ChatMessage.type for session-restore.
 * Dashboard-era only. Track 3 Slices 2.2 + 3.2.
 */
function qaToolResultToMessageType(
  qaResult: QaToolResult,
): 'query_panel_data_result' | 'aggregate_panel_data_result' {
  if (qaResult.tool === 'aggregate_panel_data') return 'aggregate_panel_data_result'
  return 'query_panel_data_result'
}

/**
 * Builds the assistant ChatMessage for a buffered Q&A tool result attached
 * to the next assistant text block. The message `type` is forced to the
 * Q&A result discriminator (so ChatPanel renders the truncation /
 * other-bucket banner), but any `choiceCards` / `choiceCardVariant`
 * surfaced by `parseChoiceCards` are preserved so a stray `[[choice]]`
 * sentinel emitted by the AI (despite `tool_choice: 'none'` on the BE
 * second turn) is not silently dropped.
 *
 * Round 2 fix for Qodo / Copilot finding on PR #389.
 */
function buildQaAttachedAssistantMessage(
  parsed: {
    type: 'text' | 'choice_cards'
    content: string
    choiceCards?: ChoiceCardOption[]
    choiceCardVariant?: ChoiceCardVariant
  },
  qaResult: QaToolResult,
): ChatMessage {
  const base: ChatMessage = {
    role: 'assistant',
    type: qaToolResultToMessageType(qaResult),
    content: parsed.content,
    qaToolResult: qaResult,
    timestamp: '',
  }
  if (parsed.choiceCards && parsed.choiceCards.length > 0) {
    base.choiceCards = parsed.choiceCards
    if (parsed.choiceCardVariant) base.choiceCardVariant = parsed.choiceCardVariant
  }
  return base
}

// ─── Session-restore lookahead helpers ───────────────────────────────────────

/**
 * Scans forward from `fromIdx` in `rawMessages` to determine whether a paired
 * assistant prose turn follows. Used to suppress raw Q&A tool_result rendering
 * when the prose turn already covers the content (TD-37).
 *
 * "Paired prose" is the next message after `fromIdx` with role === 'assistant'
 * that contains text content — either a string body or at least one `text`
 * content block. The scan stops at the first assistant message encountered.
 *
 * Returns `true` when paired prose is found; `false` when the scan reaches the
 * end of the history or the next assistant message has no text content (prose
 * composition failed mid-flight — fall through to raw rendering so the user
 * sees some content rather than nothing).
 */
function hasPairedAssistantProse(rawMessages: unknown[], fromIdx: number): boolean {
  for (let i = fromIdx + 1; i < rawMessages.length; i++) {
    const candidate = rawMessages[i]
    if (!isAnthropicMessage(candidate)) continue
    if (candidate.role !== 'assistant') continue
    // Found the next assistant message — check for text content.
    if (typeof candidate.content === 'string' && candidate.content.trim().length > 0) {
      return true
    }
    if (Array.isArray(candidate.content)) {
      const hasText = (candidate.content as AnthropicContentBlock[]).some(
        b => b.type === 'text' && typeof (b as AnthropicTextBlock).text === 'string' && (b as AnthropicTextBlock).text.trim().length > 0,
      )
      return hasText
    }
    // Next assistant message exists but has no text — prose composition failed.
    return false
  }
  // No subsequent assistant message — prose composition never happened.
  return false
}

// ─── Main parser ─────────────────────────────────────────────────────────────

/**
 * Result of parseAnthropicMessages.
 *
 * `messages`            — typed ChatMessage[] for the Redux chatSessionSlice.
 * `extractedCredentials` — raw credential options extracted from present_credential
 *                          tool-calls in the history. Callers dispatch these to
 *                          setAvailableCredentials after converting via rawToCredentialOption.
 *                          Deduped by credentialId; last occurrence wins.
 */
export interface ParseAnthropicMessagesResult {
  messages: ChatMessage[]
  extractedCredentials: RawCredentialOption[]
}

/**
 * Converts raw Anthropic-format messages (as stored in the backend JSONB column)
 * to typed ChatMessage[] for the Redux chatSessionSlice.
 *
 * Parsing rules:
 * - User string content → type: 'text' (except synthetic "(session started)")
 * - User block content → text blocks and tool_result blocks become user type: 'text'
 *   (tool_result carries pill/source selections and similar)
 * - Assistant blocks processed in order: each text block → text or choice_cards;
 *   tool_use → panel_proposals, choice_cards, filter_card, credential_card, or skipped
 * - Assistant string content → text or choice_cards
 *
 * Q&A session-restore (Track 3 Slices 2.2 + 3.2):
 * - When an assistant message contains a `tool_use` for a Q&A tool
 *   (query_panel_data or aggregate_panel_data), we record the tool_use_id → name.
 * - When the following user message contains a matching `tool_result`, we extract
 *   QaToolResult metadata and buffer it.
 * - When the next assistant text block arrives with a buffered QaToolResult, the
 *   message type is derived from `pendingQaResult.tool`:
 *     - 'query_panel_data'     → type: 'query_panel_data_result'
 *     - 'aggregate_panel_data' → type: 'aggregate_panel_data_result'
 *   This ensures the correct banner component fires on session restore.
 *
 * Timestamps are not stored in Anthropic format; restored messages set timestamp to ''.
 *
 * Also extracts RawCredentialOption[] from any present_credential tool-calls found
 * in the history, deduped by credentialId (last occurrence wins). Callers must
 * dispatch setAvailableCredentials with the converted list.
 */
export function parseAnthropicMessages(rawMessages: unknown[]): ParseAnthropicMessagesResult {
  const result: ChatMessage[] = []
  // Map preserves insertion order and provides O(1) dedup by credentialId.
  const credentialsMap = new Map<string, RawCredentialOption>()

  // Q&A session-restore state:
  // - toolUseIdToName: tracks tool_use_id → tool name for all Q&A tool_use blocks seen.
  // - pendingQaResult: a QaToolResult extracted from a user tool_result block, waiting
  //   to be attached to the next assistant text message.
  const toolUseIdToName = new Map<string, string>()
  let pendingQaResult: QaToolResult | null = null

  for (let rawIdx = 0; rawIdx < rawMessages.length; rawIdx++) {
    const raw = rawMessages[rawIdx]
    if (!isAnthropicMessage(raw)) continue

    if (raw.role === 'user') {
      if (typeof raw.content === 'string') {
        if (raw.content === '(session started)') continue
        result.push({ role: 'user', type: 'text', content: raw.content, timestamp: '' })
      } else if (Array.isArray(raw.content)) {
        for (const block of raw.content as AnthropicContentBlock[]) {
          if (isTextBlock(block)) {
            result.push({ role: 'user', type: 'text', content: block.text, timestamp: '' })
          } else if (isToolResultBlock(block)) {
            // Q&A tool result: extract metadata if this result matches a known Q&A tool.
            const toolName = block.tool_use_id ? toolUseIdToName.get(block.tool_use_id) : undefined
            if (toolName && QA_TOOL_NAMES_WITH_RESULT.has(toolName)) {
              const qaResult = extractQaToolResult(block, toolName)
              if (qaResult) {
                // Buffer for the next assistant turn — do NOT push a user message.
                pendingQaResult = qaResult
                continue
              }
              // Extraction failed (e.g. tool_result content is a raw JSON string
              // containing rows[] rather than a parsed object). Suppress raw rendering
              // when a paired assistant prose turn follows — the prose already narrates
              // the result. Fall through to legacy text rendering only when no prose
              // follows (prose composition failed mid-flight).
              if (hasPairedAssistantProse(rawMessages, rawIdx)) {
                continue
              }
            }
            // Non-Q&A tool_result (or Q&A with no paired prose): render as user text
            // (legacy: pill/source selections, or fallback for failed prose composition).
            const text = extractToolResultUserText(block)
            if (text) result.push({ role: 'user', type: 'text', content: text, timestamp: '' })
          }
        }
      }
      continue
    }

    if (raw.role === 'assistant') {
      if (typeof raw.content === 'string') {
        if (pendingQaResult) {
          // Attach the buffered QA result to this prose response. The
          // message type derives from the tool discriminator (query vs
          // aggregate). Q&A second-turn prose runs with `tool_choice:
          // 'none'` on the BE, so the AI is not expected to emit
          // `[[choice]]` sentinels here — but if it does anyway, preserve
          // the parsed `choiceCards` / `choiceCardVariant` from
          // parseChoiceCards instead of silently stripping them.
          //
          // Round 2 fix for Qodo / Copilot finding on PR #389.
          const qaResult = pendingQaResult
          pendingQaResult = null
          const parsed = parseChoiceCards(raw.content)
          result.push(buildQaAttachedAssistantMessage(parsed, qaResult))
        } else {
          const parsed = parseChoiceCards(raw.content)
          result.push({ role: 'assistant', ...parsed, timestamp: '' })
        }
        continue
      }

      if (Array.isArray(raw.content)) {
        for (const block of raw.content as AnthropicContentBlock[]) {
          if (isTextBlock(block)) {
            if (pendingQaResult) {
              // Attach the buffered QA result to the first text block in
              // this turn. See string-content branch above for the
              // parseChoiceCards rationale (preserves any stray
              // choiceCards if the AI ignores `tool_choice: 'none'`).
              const qaResult = pendingQaResult
              pendingQaResult = null
              const parsed = parseChoiceCards(block.text)
              result.push(buildQaAttachedAssistantMessage(parsed, qaResult))
            } else {
              const parsed = parseChoiceCards(block.text)
              result.push({ role: 'assistant', ...parsed, timestamp: '' })
            }
          } else if (isToolUseBlock(block)) {
            // Record Q&A tool_use_id → name for later tool_result correlation.
            if (QA_TOOL_NAMES_WITH_RESULT.has(block.name) && block.id) {
              toolUseIdToName.set(block.id, block.name)
            }
            const msg = toolUseToAssistantMessage(block)
            if (msg) result.push(msg)
            // Accumulate credential options from present_credential calls.
            // Dedup by credentialId: if the same credential is presented more
            // than once in history, the last occurrence wins (most up-to-date).
            if (block.name === 'present_credential') {
              const option = presentCredentialToOption(block.input)
              if (option) credentialsMap.set(option.id, option)
            }
          }
        }
      }
    }
  }

  return { messages: result, extractedCredentials: Array.from(credentialsMap.values()) }
}
